import React, { useEffect, useRef, useState } from "react";
import Breadcrumb from "../../components/Reusable/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchChatItem,
  fetchChatMessageList,
  fetchChatSend,
} from "../../actions/chat";
import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { formatDateFull } from "../../utils";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import { useTranslation } from "react-i18next";
import ClearIcon from "@mui/icons-material/Clear";
import {
  getFileTooLargeMessage,
  isFileTooLarge,
} from "../../utils/fileUpload";
import { toast } from "react-toastify";

const Chat = () => {
  const dispatch = useDispatch();
  const [selectedImage, setSelectedImage] = useState(null);
  const [message, setMessage] = useState("");
  const chatContainerRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(fetchChatItem());
  }, [dispatch]);

  const { chatList, chatMessagesList } = useSelector(
    (state) => state.chatReducer
  );

  // Funkcija koja postavlja scroll na dno
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  // Poziva se kada se poruke učitaju ili dodaju
  useEffect(() => {
    scrollToBottom();
  }, [chatMessagesList?.content]); // Pokreće se kad se sadržaj promeni

  const handleRemoveFile = () => {
    setSelectedImage(null); // Ukloni fajl
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = "";

    if (!file) return;

    // Isti limit važi za sve multipart slike u aplikaciji.
    if (isFileTooLarge(file)) {
      setSelectedImage(null);
      toast.error(getFileTooLargeMessage(), { theme: "colored" });
      return;
    }

    setSelectedImage(file);
  };

  const handleSendMessage = () => {
    if (isFileTooLarge(selectedImage)) {
      setSelectedImage(null);
      toast.error(getFileTooLargeMessage(), { theme: "colored" });
      return;
    }

    // Logika za slanje poruke
    setMessage(""); // Očisti input polje nakon slanja
    setSelectedImage(null); // Resetuj selektovanu sliku

    const formData = new FormData();

    if (selectedImage) {
      formData.append("fileType", "IMAGE");
      formData.append("file", selectedImage);
    }
    formData.append("message", message);
    formData.append("chatId", chatList.id);

    dispatch(fetchChatSend(formData, chatList.id));
  };

  const reloadDataChat = () => {
    dispatch(fetchChatMessageList(chatList?.id, 0, 1000));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      reloadDataChat();
    }, 30000);

    return () => clearInterval(interval);
  }, [chatList]);

  return (
    <div className="chat-main-container">
      <Breadcrumb title={t("chat.title")} returnPage="/" />

      <div className="chat-main-conetent">
        <div className="chat-center-content-header-holder-reload">
          <Tooltip
            title="Osvežite poruke"
            disableInteractive
            placement="top"
            arrow={true}
          >
            <CachedOutlinedIcon onClick={reloadDataChat} />
          </Tooltip>
        </div>
        <div
          className="chat-center-content"
          ref={chatContainerRef}
          style={{ overflowY: "auto" }}
        >
          {chatMessagesList?.content && (
            <div className="chat-center-content-list">
              {chatMessagesList?.content
                ?.slice()
                .reverse()
                .map((item) => (
                  <div
                    key={item.id}
                    className={`chat-center-content-list-item ${
                      item.senderType === "ADMINISTRATOR" ? "left" : "right"
                    }`}
                  >
                    {item.senderType === "ADMINISTRATOR" && (
                      <div className="chat-center-content-list-item-name">
                        Admin
                      </div>
                    )}
                    <div className="chat-center-content-list-item-text">
                      {item?.file && (
                        <img src={item?.file} className="img-fluid" alt="LG" />
                      )}
                      <h6>{item.message}</h6>
                      <div className="chat-center-content-list-item-time">
                        {formatDateFull(item.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className="chat-center-content-footer d-flex flex-column gap-2">
          {selectedImage && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "6px 10px",
                background: "#f9f9f9",
              }}
            >
              {/* Thumbnail */}
              <img
                src={URL.createObjectURL(selectedImage)}
                alt="preview"
                style={{
                  maxHeight: "40px",
                  marginRight: "10px",
                  borderRadius: "4px",
                }}
              />

              {/* Naziv fajla */}
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ flexGrow: 1, fontSize: "13px" }}
              >
                {selectedImage.name}
              </Typography>

              {/* Dugme za uklanjanje */}
              <IconButton
                onClick={handleRemoveFile}
                color="error"
                size="small"
                sx={{ ml: 1 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </div>
          )}
          <TextField
            placeholder={t("chat.inputText")}
            size="small"
            type="text"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={1}
            maxRows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault(); // Sprečava novi red
                handleSendMessage(); // Poziva funkciju za slanje poruke
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSendMessage} edge="end">
                    <SendIcon size="small" />
                  </IconButton>
                </InputAdornment>
              ),
              startAdornment: (
                <InputAdornment position="start">
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="upload-image"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="upload-image">
                    <IconButton component="span" color="primary">
                      <AddOutlinedIcon size="small" />
                    </IconButton>
                  </label>
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Chat;
